import { useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';

// ─── Stripe Price IDs ──────────────────────────────────────────────────────────
export const STRIPE_PRICE_ANNUAL  = 'price_1Tr6mADcm5b2lARuE3aEUpTM';
export const STRIPE_PRICE_MONTHLY = 'price_1Tr6klDcm5b2lARu9WkfBmjO';
// ─────────────────────────────────────────────────────────────────────────────

export function useStripe(userId) {
    const [purchasing, setPurchasing] = useState(false);
    const [error, setError] = useState(null);

    /**
     * Redirects the user to Stripe Checkout for the selected plan.
     * The Supabase Edge Function creates the session and returns the URL.
     * After payment, Stripe redirects back to the app.
     */
    const purchaseStripe = useCallback(async (priceId) => {
        if (!userId) {
            setError('Please sign in before purchasing.');
            return { success: false };
        }
        setPurchasing(true);
        setError(null);
        try {
            const successUrl = `${window.location.origin}/?payment=success`;
            const cancelUrl  = `${window.location.origin}/?payment=cancelled`;

            const { data, error: fnError } = await supabase.functions.invoke('stripe-checkout', {
                body: { priceId, userId, successUrl, cancelUrl },
            });

            if (fnError || !data?.url) {
                throw new Error(fnError?.message || 'Could not create checkout session.');
            }

            // Redirect to Stripe-hosted checkout page
            window.location.href = data.url;
            return { success: true };
        } catch (e) {
            console.error('Stripe checkout error:', e);
            setError(e.message || 'Payment failed. Please try again.');
            return { success: false };
        } finally {
            setPurchasing(false);
        }
    }, [userId]);

    /**
     * Redirect to Stripe Customer Portal so users can manage / cancel their subscription.
     */
    const manageSubscription = useCallback(async () => {
        if (!userId) return;
        setPurchasing(true);
        try {
            const returnUrl = window.location.origin;
            const { data, error: fnError } = await supabase.functions.invoke('stripe-portal', {
                body: { userId, returnUrl },
            });
            if (fnError || !data?.url) throw new Error(fnError?.message || 'Could not open portal.');
            window.location.href = data.url;
        } catch (e) {
            setError(e.message || 'Could not open subscription management.');
        } finally {
            setPurchasing(false);
        }
    }, [userId]);

    return { purchasing, error, setError, purchaseStripe, manageSubscription };
}
